import {
  registerEmail as registerEmailApi,
  verifyRegistration as verifyRegistrationApi,
  login as loginApi,
  forgotPassword as forgotPasswordApi,
  forgotPasswordVerify as forgotPasswordVerifyApi,
  postOnboarding as postOnboardingApi,
  logout as logoutApi,
  changePassword as changePasswordApi,
  verifyInvite as verifyInviteApi,
  verifyInviteExisting as verifyInviteExistingApi,
  unblockUser as unblockUserApi
} from '../api/authentication'

import { refreshTokenOnce } from '../api/refresh_token'

import { IPostOnboardingRequest } from '../api/types/sessions'
import { IJWTToken } from '../models/token'
import { IUser } from '../models/user'
import {
  setResourceAuthorization,
  setResourceAuthorizations
} from '../state/slices/resource_authorization_slice'
import { setTerms } from '../state/slices/terms_slice'
import { setUser } from '../state/slices/user_slice'
import { useAppDispatch } from './state'
import {
  logout as logoutAction
} from '../state/actions/constants'
import usePolicies from './use_policies'
import useAgency from './use_agency'
import {
  ILoginApiResponse,
  ILoginApiSuccessResponse
} from '../api/types/login_api'
import useProducts from './use_product'
import { setToken } from '../state/slices/token_slice'
import useNotification from './use_notification'
import { getStore } from '../state/configure_store'
import {
  createAuthSessionSnapshot,
  getAuthSessionSnapshotStatus
} from '../utils/auth_session'

type ApiError = { message?: string };
type AuthResult = { success: boolean; message: string; user: IUser | null };

const DEFAULT_ERROR_MESSAGE =
  'Er is iets misgegaan. Probeer het later opnieuw.'

const errorMessage = (error: unknown) =>
  (error as ApiError)?.message || DEFAULT_ERROR_MESSAGE

export const useAuthentication = () => {
  const dispatch = useAppDispatch()
  const { getPolicies, getIVBConfig } = usePolicies()
  const { getAgency } = useAgency()
  const { getProducts, getAdviceProducts } = useProducts()
  const { getNotifications } = useNotification()

  const handleLoginSuccess = async (
    user: IUser,
    token?: IJWTToken
  ): Promise<AuthResult> => {
    if (token) dispatch(setToken(token))

    dispatch(setUser(user))
    dispatch(setTerms(user.term))

    dispatch(setResourceAuthorization(user.currentResourceAuthorization))
    dispatch(setResourceAuthorizations(user.resourceAuthorizations ?? []))

    getPolicies(user.currentResourceAuthorization?.relationNumber)
    getIVBConfig()
    getProducts()
    await getAdviceProducts(user.currentResourceAuthorization)
    await getNotifications()

    const agentNumber =
      user.currentResourceAuthorization?.relationInfo?.agentNumber

    if (agentNumber) await getAgency(agentNumber)

    return { success: true, message: '', user }
  }

  /**
   * A generic auth flow method to handle several authentication requests
   * @param fn Api request with ILoginApiResposne
   * @returns success message
   */
  const tryAuthFlow = async (
    fn: () => Promise<ILoginApiResponse<ILoginApiSuccessResponse>>
  ): Promise<AuthResult> => {
    const { data } = await fn()
    const { user, jsonWebToken } = data ?? {}

    if (user && jsonWebToken) {
      return await handleLoginSuccess(user, jsonWebToken)
    }

    throw new Error(DEFAULT_ERROR_MESSAGE)
  }

  const registerEmail = (email: string) => registerEmailApi(email)

  const forgotPassword = (email: string) => forgotPasswordApi(email)

  const forgotPasswordVerify = (
    email: string,
    newPassword: string,
    repeatNewPassword: string,
    token: string
  ) => forgotPasswordVerifyApi(email, newPassword, repeatNewPassword, token)

  const logout = async () => {
    try {
      await logoutApi()
    } finally {
      dispatch(logoutAction())
    }
  }

  const login = (email: string, password: string) =>
    tryAuthFlow(() => loginApi(email, password))

  const verifyRegistration = (
    email: string,
    newPassword: string,
    repeatNewPassword: string,
    registerToken: string
  ) =>
    tryAuthFlow(() =>
      verifyRegistrationApi(
        email,
        newPassword,
        repeatNewPassword,
        registerToken
      )
    )

  const verifyInvite = (
    email: string,
    relationNumber: string,
    token: string,
    newPassword: string,
    repeatNewPassword: string
  ) =>
    tryAuthFlow(() =>
      verifyInviteApi(
        email,
        relationNumber,
        token,
        'new_user',
        newPassword,
        repeatNewPassword
      )
    )

  const verifyInviteExisting = (
    email: string,
    relationNumber: string,
    token: string
  ) => verifyInviteExistingApi(email, relationNumber, token, 'existing_user')

  const unblockUser = (
    email: string,
    password: string,
    passwordConfirmation: string,
    token: string
  ) =>
    tryAuthFlow(() =>
      unblockUserApi(email, password, passwordConfirmation, token)
    )

  const postOnboarding = async (
    data: IPostOnboardingRequest
  ): Promise<AuthResult> => {
    try {
      const { data: payload } = await postOnboardingApi(data)
      const { user, jsonWebToken } = payload ?? {}

      if (user) {
        return await handleLoginSuccess(user, jsonWebToken)
      }

      return { success: false, message: DEFAULT_ERROR_MESSAGE, user: null }
    } catch (e) {
      return { success: false, message: errorMessage(e), user: null }
    }
  }

  const changePassword = async (
    oldPassword: string,
    password: string,
    passwordConfirmation: string
  ) => {
    const result = await changePasswordApi(
      oldPassword,
      password,
      passwordConfirmation
    )
    const { user, jsonWebToken } = result?.data ?? {}

    if (user && jsonWebToken) {
      await handleLoginSuccess(user, jsonWebToken)
    }

    return result
  }

  const refreshSession = async () => {
    const store = getStore()
    const snapshot = createAuthSessionSnapshot(store.getState())

    if (!snapshot) {
      return
    }

    const { data } = await refreshTokenOnce(snapshot.refreshToken)
    const { user, jsonWebToken } = data ?? {}
    const status = getAuthSessionSnapshotStatus(store.getState(), snapshot)

    if (status !== 'current') {
      return
    }

    if (user && jsonWebToken) {
      return await handleLoginSuccess(user, jsonWebToken)
    }

    throw new Error(DEFAULT_ERROR_MESSAGE)
  }

  return {
    registerEmail,
    verifyRegistration,
    login,
    logout,
    forgotPassword,
    forgotPasswordVerify,
    postOnboarding,
    changePassword,
    verifyInvite,
    verifyInviteExisting,
    unblockUser,
    refreshSession
  }
}

export default useAuthentication
